/*
 OK FBI — Investigator Meeting Room + compact TikTok LIVE strip (Host)
 غرفة اجتماع المحققين — real-time TikTok LIVE comments with vote-parse badges.

 Two exports:
   • TikTokConnectStrip  — compact host-only bar at the top of the case page
   • InvestigatorMeetingRoom  — full comment room shown to the host
 Both refuse to fake a "connected/LIVE" status. Green appears only when a real
 comment has arrived within STALE window (backend computes real_live).
*/
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/pages/AuthPage";
import { StreamPlatformsModal } from "@/components/streamer/StreamPlatformsModal";
import { Radio, Wifi, WifiOff, RefreshCcw, TestTube2, User, Users, Sparkles, CircleDot, Copy, Check, AlertTriangle, Megaphone, Eye, EyeOff, Video } from "lucide-react";

// ---------- helpers ----------
function fmtClock(iso) {
  try { return new Date(iso).toLocaleTimeString("ar-EG", { hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
}
function fmtClockFull(iso) {
  try { return new Date(iso).toLocaleTimeString("ar-EG", { hour: "2-digit", minute: "2-digit", second: "2-digit" }); }
  catch { return "—"; }
}

// Truthful connection state derived from backend `/tiktok/status`.
// active states: offline | connecting | armed | live | dropped
function deriveState(conn) {
  if (!conn) return { code: "offline", label: "غير متصل", tone: "gray", dot: "⚪" };
  if (conn.connecting) return { code: "connecting", label: "جاري الاتصال...", tone: "amber", dot: "🟡" };
  if (!conn.connected) {
    if (conn.last_error) return { code: "dropped", label: "انقطع الاتصال", tone: "red", dot: "🔴" };
    return { code: "offline", label: "غير متصل", tone: "gray", dot: "⚪" };
  }
  if (conn.real_live) return { code: "live", label: "متصل بالبث", tone: "emerald", dot: "🟢" };
  // armed but no recent comments — could be viewer silent OR account not actually live
  return { code: "armed", label: "متصل · بانتظار تعليقات حقيقية", tone: "amber", dot: "🟡" };
}
const TONE_CLASS = {
  gray:    "text-zinc-400 border-zinc-700 bg-zinc-900/40",
  amber:   "text-amber-300 border-amber-700 bg-amber-950/20",
  emerald: "text-emerald-300 border-emerald-700 bg-emerald-950/20",
  red:     "text-red-300 border-red-800 bg-red-950/20",
};

// =====================================================================
//   Compact host connect strip — inside Operations Room
//   Shows ONLY TikTok LIVE identity. Any external bridge software is
//   completely hidden from the UI (host and viewers). Errors are rendered
//   with neutral wording — no bridge/webhook/connector names.
// =====================================================================
export function TikTokConnectStrip({ caseId, suspects }) {
  const { user, isOwner } = useAuth();
  const isStreamerOrOwner = isOwner
    || user?.role === "OWNER"
    || user?.account_type === "OWNER"
    || user?.role === "STREAMER"
    || user?.account_type === "STREAMER"
    || (user?.membership_plan || "").toUpperCase() === "STREAMER";

  const [conn, setConn] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    if (!isStreamerOrOwner) return; // FREE/VIP never poll — no data leak, no 403 noise
    try {
      const { data } = await api.get("/tiktok/status");
      setConn(data);
    } catch {}
  }, [isStreamerOrOwner]);

  useEffect(() => { load(); const iv = setInterval(load, 2500); return () => clearInterval(iv); }, [load]);

  // Phase 2 role isolation: FREE / VIP see nothing at all.
  if (!isStreamerOrOwner) return null;

  const st = deriveState(conn);

  const doDisconnect = async () => {
    try {
      await api.post("/tiktok/disconnect");
      load();
      toast.success("تم قطع الاتصال");
    } catch {}
  };

  const doReconnect = async () => {
    try {
      await api.post("/tiktok/reconnect");
      load();
      toast.success("جاري إعادة الاتصال");
    } catch {}
  };

  // Truthful comment sub-status
  const commentsStatus =
    !conn?.connected ? { dot: "⚪", text: "لا توجد", tone: "gray" }
      : conn?.real_live ? { dot: "🟢", text: "تصل الآن", tone: "emerald" }
      : { dot: "⚪", text: "بانتظار أول تعليق", tone: "gray" };

  return (
    <div className={`panel p-3 border ${TONE_CLASS[st.tone] || TONE_CLASS.gray}`} data-testid="tiktok-strip">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 shrink-0">
          <Radio size={13} className="text-gold" />
          <span className="text-[11px] tracking-[0.28em] text-gold font-mono-ar">منصات البث</span>
        </div>

        {/* Status pill */}
        <div className={`text-[11px] font-mono-ar px-2 py-1 border ${TONE_CLASS[st.tone]}`} data-testid="tiktok-status">
          <span className="align-middle mr-1">{st.dot}</span>
          <span className="align-middle font-cairo">
            {conn?.connected
              ? `TikTok: متصل (${conn.display_name || conn.username || "موثّق"})`
              : "منصات البث · غير متصل"}
          </span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 mr-auto">
          {!conn?.connected ? (
            <button
              onClick={() => setModalOpen(true)}
              data-testid="tiktok-connect"
              className="btn-primary text-[11px] py-1 px-3 flex items-center gap-1.5 cursor-pointer"
            >
              <Wifi size={11} /> ربط منصة البث
            </button>
          ) : (
            <>
              <button
                onClick={() => setModalOpen(true)}
                className="text-[10px] font-cairo border border-metal py-1 px-2.5 text-gold hover:bg-gold/10 flex items-center gap-1 cursor-pointer"
              >
                <Video size={10} /> إدارة المنصات
              </button>
              <button
                onClick={doReconnect}
                data-testid="tiktok-reconnect"
                className="text-[10px] font-cairo border border-metal py-1 px-2 text-zinc-300 hover:border-gold hover:text-gold flex items-center gap-1 cursor-pointer"
              >
                <RefreshCcw size={10} /> إعادة
              </button>
              <button
                onClick={doDisconnect}
                data-testid="tiktok-disconnect"
                className="text-[10px] font-cairo border border-red-900 py-1 px-2 text-red-400 hover:bg-red-950/30 flex items-center gap-1 cursor-pointer"
              >
                <WifiOff size={10} /> قطع الاتصال
              </button>
            </>
          )}
        </div>
      </div>

      {/* Truthful comment-sub-status row */}
      {conn?.connected && (
        <div className="mt-2 flex items-center gap-4 text-[10px] font-cairo">
          <div className="text-zinc-500">
            التعليقات: <span className={`font-mono-ar ${commentsStatus.tone === "emerald" ? "text-emerald-300" : "text-zinc-400"}`}>
              {commentsStatus.dot} {commentsStatus.text}
            </span>
          </div>
        </div>
      )}

      <StreamPlatformsModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onRefresh={load}
      />
    </div>
  );
}

// =====================================================================
//   Investigator Meeting Room — full comment feed w/ vote badges
// =====================================================================
export function InvestigatorMeetingRoom({ caseId, suspects, mini = false, showControls = true }) {
  const { user, isOwner } = useAuth();
  const isStreamerOrOwner = isOwner || (user?.membership_plan || "").toUpperCase() === "STREAMER";
  const [conn, setConn] = useState(null);
  const [comments, setComments] = useState([]);
  const [pulseAt, setPulseAt] = useState(0);
  const [testStatus, setTestStatus] = useState(null); // { active, resolved, key }
  const feedRef = useRef(null);
  const lastCommentIdRef = useRef(null);

  // Poll status + comments together
  const load = useCallback(async () => {
    if (!isStreamerOrOwner) return; // FREE / VIP never poll
    try {
      const [s, c, tw] = await Promise.all([
        api.get("/tiktok/status").catch(() => ({ data: null })),
        api.get("/tiktok/comments?filter=all&limit=50").catch(() => ({ data: [] })),
        api.get("/tiktok/test-watch/status").catch(() => ({ data: null })),
      ]);
      setConn(s.data);
      const list = (c.data || []).slice().reverse();  // oldest → newest for auto-scroll to bottom
      setComments(list);
      setTestStatus(tw?.data || null);
      // Gold pulse on new comment
      const newest = list[list.length - 1];
      if (newest && newest.id !== lastCommentIdRef.current) {
        lastCommentIdRef.current = newest.id;
        setPulseAt(Date.now());
      }
    } catch {}
  }, [isStreamerOrOwner]);
  useEffect(() => { load(); const iv = setInterval(load, 2000); return () => clearInterval(iv); }, [load]);

  // Auto-scroll to bottom on new comment
  useEffect(() => {
    if (!feedRef.current) return;
    feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, [comments.length]);

  // Phase 2 role isolation: FREE / VIP see nothing.
  if (!isStreamerOrOwner) return null;

  const st = deriveState(conn);
  const total = conn?.comment_count || 0;
  const staleSec = conn?.stale_seconds ?? null;
  const showNoCommentsWarn =
    conn?.connected && conn?.real_live === false && staleSec !== null && staleSec > 45 && total > 0;

  // Test-comment flow (real, not simulated)
  const beginTest = async () => {
    if (!conn?.connected) { toast.error("افتح الاتصال بالبث أولاً"); return; }
    try {
      await api.post("/tiktok/test-watch/begin", { key: "TEST123" });
      load();
      toast.info("اكتب TEST123 في بث TikTok الآن");
    } catch { toast.error("تعذّر بدء اختبار التعليقات"); }
  };
  const cancelTest = async () => {
    try { await api.post("/tiktok/test-watch/cancel"); load(); } catch {}
  };
  const testActive = !!(testStatus && testStatus.active && !testStatus.resolved);
  const testResolved = !!(testStatus && testStatus.resolved);

  // suspect index/name helpers for vote badges
  const suspectIndex = (sid) => {
    if (!suspects) return null;
    const i = suspects.findIndex(s => s.id === sid);
    return i >= 0 ? i + 1 : null;
  };
  const suspectName = (sid) => {
    if (!suspects) return null;
    const s = suspects.find(x => x.id === sid);
    return s?.name || null;
  };

  // Pulse class: brief 900ms gold flash on header when a new comment arrives
  const pulsing = Date.now() - pulseAt < 900;

  return (
    <div className={`panel ${mini ? "p-3" : "p-4"} border-metal`} data-testid="meeting-room">
      {/* Header */}
      <div className={`flex items-center justify-between mb-3 rounded-sm transition-shadow ${pulsing ? "shadow-[0_0_0_1px_rgba(197,160,89,0.6),0_0_18px_rgba(197,160,89,0.35)]" : ""}`}>
        <div className="flex items-center gap-2 min-w-0">
          <Users size={mini ? 12 : 14} className="text-gold shrink-0" />
          <div className="min-w-0">
            <div className={`${mini ? "text-[11px]" : "text-[12px]"} tracking-[0.25em] text-gold font-cairo`}>غرفة اجتماع المحققين</div>
            {!mini && <div className="text-[9px] text-zinc-500 font-cairo">تعليقات المحققين مباشرة من TikTok LIVE</div>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {conn?.username && <div className="text-[10px] font-mono-ar text-gold">@{conn.username}</div>}
          <div className={`text-[10px] font-mono-ar px-2 py-0.5 border ${TONE_CLASS[st.tone]}`} data-testid="meeting-room-status">
            <span className="mr-1 align-middle">{st.dot}</span>
            <span className="font-cairo align-middle">
              {st.code === "live" ? "بث مباشر متصل" : st.code === "armed" ? "متصل · بانتظار تعليقات" : st.code === "offline" ? "منصات البث · غير متصل" : st.label}
            </span>
          </div>
        </div>
      </div>

      {/* Warning: connected but no comments for a while */}
      {showNoCommentsWarn && (
        <div className="mb-3 p-2 border border-amber-800 bg-amber-950/20 text-amber-300 text-[11px] font-cairo flex items-center gap-2" data-testid="meeting-no-comments-warn">
          <AlertTriangle size={12} /> لا تصل تعليقات من TikTok حالياً · تأكد من أن البث نشط
        </div>
      )}

      {/* Test comment banner */}
      {testActive && !testResolved && (
        <div className="mb-3 p-2 border border-gold/60 bg-[rgba(197,160,89,0.08)] text-[11px] font-cairo flex items-center justify-between" data-testid="test-watch-active">
          <div className="text-zinc-200">
            <span className="text-gold">اكتب في بث TikTok الآن:</span>{" "}
            <span className="font-mono-ar text-white bg-black/50 px-2 py-0.5 border border-gold/40">{testStatus?.key || "TEST123"}</span>
          </div>
          <button onClick={cancelTest} className="text-[10px] text-zinc-400 hover:text-red-300 border border-metal px-2 py-0.5">إلغاء</button>
        </div>
      )}
      {testResolved && (
        <div className="mb-3 p-2 border border-emerald-700 bg-emerald-950/20 text-emerald-300 text-[11px] font-cairo flex items-center gap-2" data-testid="test-watch-resolved">
          <Check size={12} /> الاتصال يعمل — تم استلام تعليق حقيقي من TikTok LIVE
          <button onClick={cancelTest} className="mr-auto text-[10px] text-zinc-400 hover:text-white border border-metal px-2 py-0.5">إغلاق</button>
        </div>
      )}

      {/* Feed */}
      <div
        ref={feedRef}
        className={`overflow-auto space-y-0 ${mini ? "max-h-[220px]" : "max-h-[360px]"} pr-1`}
        data-testid="meeting-room-feed"
      >
        {comments.length === 0 && (
          <div className="text-center py-8 text-zinc-500 text-[12px] font-cairo">
            لا توجد تعليقات بعد — سيظهر أول تعليق حقيقي هنا لحظياً.
          </div>
        )}
        {comments.map(c => {
          const isVote = !!c.matched;
          const idx = isVote ? suspectIndex(c.matched) : null;
          const nm = isVote ? suspectName(c.matched) : null;
          const changed = !!(c.prev_matched && c.prev_matched !== c.matched);
          const prevIdx = changed ? suspectIndex(c.prev_matched) : null;
          return (
            <div
              key={c.id}
              className={`flex items-start gap-2 py-1.5 border-b border-metal/40 text-[12px] font-cairo ${isVote ? "border-r border-r-gold/40 bg-[rgba(197,160,89,0.04)] pr-2" : ""}`}
              data-testid={`meeting-comment-${c.id}`}
            >
              <div className="w-7 h-7 rounded-full border border-metal bg-black/60 flex items-center justify-center overflow-hidden shrink-0">
                {c.avatar ? <img src={c.avatar} alt="" className="w-full h-full object-cover" /> : <User size={12} className="text-zinc-500" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-gold text-[11px] truncate max-w-[140px]">{c.display_name || c.username}</span>
                  <span className="text-zinc-600 text-[9px] font-mono-ar shrink-0">{fmtClock(c.at)}</span>
                </div>
                <div className="text-white break-words">{c.text}</div>
                {/* Vote badge */}
                {isVote && !c.duplicate_vote && !changed && (
                  <div className="text-[10px] text-emerald-300 mt-0.5 flex items-center gap-1" data-testid="meeting-vote-recognized">
                    <Sparkles size={10} />
                    ✓ تم تسجيل التصويت{nm ? ` لـ ${nm}` : idx ? ` للمشتبه به رقم ${idx}` : ""}
                  </div>
                )}
                {isVote && changed && (
                  <div className="text-[10px] text-gold mt-0.5" data-testid="meeting-vote-changed">
                    <span>{c.display_name || c.username} غيّر تصويته: </span>
                    <span className="font-mono-ar">{prevIdx ?? "?"} → {idx ?? "?"}</span>
                  </div>
                )}
                {isVote && c.duplicate_vote && (
                  <div className="text-[10px] text-zinc-400 mt-0.5" data-testid="meeting-vote-duplicate">
                    التصويت مسجل مسبقاً
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer strip */}
      {showControls && (
        <div className="mt-3 pt-2 border-t border-metal/50 flex items-center gap-3 flex-wrap text-[10px] font-cairo">
          <div className="text-zinc-500">
            آخر تعليق: <span className="text-white font-mono-ar">{conn?.last_comment_at ? fmtClockFull(conn.last_comment_at) : "—"}</span>
          </div>
          <div className="text-zinc-500">
            إجمالي التعليقات: <span className="text-white font-mono-ar tabular-nums">{total}</span>
          </div>
          <div className="mr-auto flex items-center gap-1">
            <button onClick={beginTest} disabled={testActive}
              data-testid="btn-test-comments"
              className={`text-[11px] px-3 py-1 border flex items-center gap-1 ${testActive ? "border-metal text-zinc-500 cursor-wait" : "border-gold text-gold hover:bg-[rgba(197,160,89,0.12)]"}`}>
              <TestTube2 size={11} /> اختبار التعليقات
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
